import { useMemo, useState } from 'react'
import { Plus, RefreshCw, AlertCircle, Search, User } from 'lucide-react'
import { toast } from 'sonner'
import { useAdminArticles, useAssignArticle, useDeleteAdminArticle } from '../api/hooks'
import type { WireArticle, WireWorkflowStage, RoleAssignments } from '../api/types'
import { useWireAuth } from '../auth/useWireAuth'
import { NewArticleDialog } from '../components/articles/NewArticleDialog'
import { PipelineBoard } from '../components/pipeline/PipelineBoard'

/** Stages shown on this screen — pre-publication work in progress. */
const IN_PROGRESS_STAGES = new Set<WireWorkflowStage>([
  'qc_review', 'layout_review', 'editorial_approval', 'scheduled', 'post_pub_review',
])

function isAssignedToUser(article: WireArticle, sub: string): boolean {
  return [
    article.workflow.qcAssignedTo,
    article.workflow.layoutAssignedTo,
    article.workflow.editorialAssignedTo,
    article.workflow.postPubAssignedTo,
  ].includes(sub)
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ArticleListPage() {
  const { user } = useWireAuth()
  const currentUserSub = user?.username ?? user?.sub ?? ''

  const { data: allArticles = [], isPending, isError, error, refetch, isFetching } = useAdminArticles()
  const { mutateAsync: assignArticle } = useAssignArticle()
  const { mutateAsync: deleteArticle } = useDeleteAdminArticle()

  const [open, setOpen] = useState(false)
  const [onlyMine, setOnlyMine] = useState(false)
  const [search, setSearch] = useState('')

  const inProgress = useMemo(
    // Compatibility: if backend still returns stage='blocked', keep article visible
    // in Copy Desk when an active block exists.
    () => allArticles.filter((a) => a.workflow && (IN_PROGRESS_STAGES.has(a.workflow.stage) || Boolean(a.workflow.activeBlock))),
    [allArticles]
  )

  const displayArticles = useMemo(() => {
    let result = inProgress
    if (onlyMine) result = result.filter((a) => isAssignedToUser(a, currentUserSub))
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      result = result.filter((a) =>
        a.title.toLowerCase().includes(q) ||
        (a.documentId ?? '').toLowerCase().includes(q)
      )
    }
    return result
  }, [inProgress, onlyMine, search, currentUserSub])

  async function handleReassignToMe(article: WireArticle) {
    const { stage, qcAssignedTo, layoutAssignedTo, editorialAssignedTo, postPubAssignedTo } = article.workflow
    const assignments: RoleAssignments = {
      qcAssignedTo:        qcAssignedTo        ?? null,
      layoutAssignedTo:    layoutAssignedTo    ?? null,
      editorialAssignedTo: editorialAssignedTo ?? null,
      postPubAssignedTo:   postPubAssignedTo   ?? null,
    }
    if (stage === 'qc_review')               assignments.qcAssignedTo        = currentUserSub
    else if (stage === 'layout_review')      assignments.layoutAssignedTo    = currentUserSub
    else if (stage === 'editorial_approval') assignments.editorialAssignedTo = currentUserSub
    else if (stage === 'post_pub_review')    assignments.postPubAssignedTo   = currentUserSub
    else return

    try {
      await assignArticle({ id: article.id, assignments })
      toast.success('Reassigned to you', { description: article.title })
    } catch (err) {
      toast.error('Reassignment failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  async function handleDelete(article: WireArticle) {
    try {
      await deleteArticle(article.id)
      toast.success('Draft deleted', { description: `"${article.title}" was deleted.` })
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  async function handleAssign(article: WireArticle, assignments: RoleAssignments) {
    try {
      await assignArticle({ id: article.id, assignments })
      toast.success('Assignments saved', { description: article.title })
    } catch (err) {
      toast.error('Assignment failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  return (
    <div className="relative">

      {/* Hero gradient wash — full viewport width */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-56"
        style={{ background: 'linear-gradient(to bottom, var(--violet-4), var(--violet-2), transparent)', opacity: 0.5 }}
      />

      {/* Header */}
      <div className="relative px-6 pt-5">
        <div className="flex items-center justify-between border-b border-line pb-4">
          {/* Left: title + filters */}
          <div className="flex items-center gap-8">
            <h1 className="font-zodiak pl-8 text-3xl font-bold text-fg">Copy Desk</h1>

            {/* ── Filters ── */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative flex items-center">
                <Search size={12} className="pointer-events-none absolute left-2.5 text-fg-secondary" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter articles…"
                  className="h-8 w-52 rounded border border-line bg-surface pl-7 pr-3 text-xs text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
                />
              </div>

              {/* Mine toggle */}
              <button
                type="button"
                onClick={() => setOnlyMine((v) => !v)}
                className={`flex h-8 items-center gap-1.5 rounded border px-3 text-xs font-semibold transition-colors ${
                  onlyMine
                    ? 'border-violet-400 bg-violet-50 text-violet-700 dark:border-violet-600 dark:bg-violet-950 dark:text-violet-300'
                    : 'border-line bg-surface text-fg-secondary hover:border-line-strong hover:text-fg'
                }`}
                aria-pressed={onlyMine}
              >
                <User size={11} aria-hidden="true" />
                Only My Articles
                {onlyMine && (
                  <span className="rounded-full bg-violet-200 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-violet-700 dark:bg-violet-800 dark:text-violet-200">
                    {displayArticles.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="flex h-8 w-8 items-center justify-center rounded border border-line bg-surface text-fg-secondary transition-colors hover:border-line-strong hover:text-fg disabled:opacity-50"
              aria-label="Refresh"
            >
              <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="flex h-8 items-center gap-2 rounded border border-line bg-surface px-3 text-xs font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill"
            >
              <Plus size={12} aria-hidden="true" />
              New Article
            </button>
          </div>
        </div>

        {/* Error state */}
        {isError && (
          <div className="mt-6 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            <AlertCircle size={14} aria-hidden="true" />
            {error instanceof Error ? error.message : 'Failed to load articles'}
          </div>
        )}

        {/* Loading skeleton */}
        {isPending && (
          <div className="mt-6 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded bg-fill" />
            ))}
          </div>
        )}

      </div>

      {/* Pipeline board — full width */}
      {!isPending && (
        <div className="px-6 pb-8 pt-5">
          <PipelineBoard
            articles={displayArticles}
            currentUserSub={currentUserSub}
            onReassignToMe={handleReassignToMe}
            onDelete={handleDelete}
            onAssign={handleAssign}
          />
        </div>
      )}

      <NewArticleDialog open={open} onOpenChange={setOpen} />
    </div>
  )
}
